import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ICONS as Icons } from "../data/iconRegistry";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { getNetworkMeta } from "../data/networksMeta";
import PricingCard from "../components/network/PricingCard";
import ComparisonTable from "../components/network/ComparisonTable";
import Guarantees from "../components/network/Guarantees";
import ComboBundles from "../components/network/ComboBundles";
import ReviewsSection from "../components/network/ReviewsSection";
import LiveSocialProof from "../components/network/LiveSocialProof";
import HowItOrderWorks from "../components/network/HowItOrderWorks";
import SEOHead from "../components/SEOHead";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import NotFound from "./NotFound";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const VARIANT_ORDER = ["monde", "europe", "france"];
const VARIANT_WORD_RE = /\s+(Monde|Europe|France|Worldwide)$/i;
const VARIANT_FLAG = { monde: "\u{1F30D}", europe: "\u{1F1EA}\u{1F1FA}", france: "\u{1F1EB}\u{1F1F7}" };
const CATEGORY_PRIORITY = ["followers", "subscribers", "members", "likes", "views", "streams", "connections", "viewers", "repins", "plays", "comments"];
const EXPLICIT_PRIORITY = ["followers", "subscribers", "views", "members", "followers_premium", "followers_europe", "followers_vip_france_homme", "followers_vip_france_femme", "likes", "likes_page", "likes_post", "likes_top", "likes_premium", "views_top", "views_premium", "reactions", "comments", "watch_hours", "video_views", "group_members", "shares", "live_views", "favorites", "retweets"];
const SERVICE_TYPE_ICON = { followers: "UserPlus", subscribers: "UserPlus", members: "Users", likes: "Heart", views: "Eye", streams: "PlayCircle", connections: "Link2", viewers: "Eye", repins: "Image", plays: "PlayCircle", reactions: "ThumbsUp", comments: "MessageCircle", group_members: "Users", shares: "Share2", video_views: "Eye", live_views: "Radio", favorites: "Star", watch_hours: "Clock", retweets: "Repeat2", listeners: "Headphones", saves: "Bookmark", reposts: "Repeat2", direct: "TrendingUp", organic: "Globe", mobile: "Eye", geo: "Link2" };
const SERVICE_TYPE_LABEL_FR = { followers: "Followers", subscribers: "Abonnés", members: "Membres", likes: "Likes", views: "Vues", streams: "Streams", connections: "Connexions", viewers: "Viewers", repins: "Repins", plays: "Plays", reactions: "Réactions", comments: "Commentaires", group_members: "Membres", shares: "Partages", video_views: "Vues", live_views: "Vues Live", favorites: "Favoris", watch_hours: "Heures", retweets: "Retweets", listeners: "Auditeurs", saves: "Enregistrements", reposts: "Reposts", direct: "Trafic Direct", organic: "Trafic Organique", mobile: "Trafic Mobile", geo: "Trafic Géolocalisé" };
const SERVICE_TYPE_LABEL_EN = { followers: "Followers", subscribers: "Subscribers", members: "Members", likes: "Likes", views: "Views", streams: "Streams", connections: "Connections", viewers: "Viewers", repins: "Repins", plays: "Plays", reactions: "Reactions", comments: "Comments", group_members: "Members", shares: "Shares", video_views: "Views", live_views: "Live Views", favorites: "Favorites", watch_hours: "Hours", retweets: "Retweets", listeners: "Listeners", saves: "Saves", reposts: "Reposts", direct: "Direct Traffic", organic: "Organic Traffic", mobile: "Mobile Traffic", geo: "Geo-Targeted Traffic" };
const categoryPriority = (gk) => {
  const explicitIdx = EXPLICIT_PRIORITY.indexOf(gk);
  if (explicitIdx !== -1) return explicitIdx;
  const idx = CATEGORY_PRIORITY.findIndex((p) => gk.startsWith(p));
  return idx === -1 ? 999 : idx + 100;
};
const serviceTypeOf = (gk) => CATEGORY_PRIORITY.find((p) => gk.startsWith(p)) || gk;

function parseCategory(product, field) {
  const parts = product.category.split("_");
  const last = parts[parts.length - 1];
  if (VARIANT_ORDER.includes(last) && parts.length > 1) {
    const groupKey = parts.slice(0, -1).join("_");
    const label = field(product, "category_label").replace(VARIANT_WORD_RE, "");
    return { groupKey, variantKey: last, groupLabel: label };
  }
  return { groupKey: product.category, variantKey: "default", groupLabel: field(product, "category_label") };
}

export default function NetworkPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { field, t, language } = useLanguage();
  const [network, setNetwork] = useState(null);
  const [products, setProducts] = useState([]);
  const [faq, setFaq] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [groupKey, setGroupKey] = useState(null);
  const [serviceType, setServiceType] = useState(null);
  const [variantKey, setVariantKey] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [allNetworks, setAllNetworks] = useState([]);

  useEffect(() => {
    setSelected(null); setNetwork(null); setNotFound(false); setGroupKey(null); setServiceType(null); setVariantKey(null);
    api.get(`/networks/${slug}`).then(({ data }) => setNetwork(data)).catch(() => setNotFound(true));
    api.get("/products", { params: { network: slug } }).then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/faq").then(({ data }) => setFaq(data.slice(0, 5))).catch(() => {});
    api.get("/blog").then(({ data }) => setBlogPosts(data.slice(0, 3))).catch(() => {});
  }, [slug]);

  useEffect(() => {
    api.get("/networks").then(({ data }) => setAllNetworks(data)).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    products.filter((p) => p.category !== "combo").forEach((p) => {
      const { groupKey: gk, variantKey: vk, groupLabel } = parseCategory(p, field);
      if (!map.has(gk)) map.set(gk, { label: groupLabel, variants: new Map() });
      const g = map.get(gk);
      if (!g.variants.has(vk)) g.variants.set(vk, { label: p.country_target || null, products: [] });
      g.variants.get(vk).products.push(p);
    });
    return map;
  }, [products, field]);

  const comboProducts = useMemo(() => products.filter((p) => p.category === "combo"), [products]);

  useEffect(() => {
    if (searchParams.get("combo") === "1" && comboProducts.length > 0) {
      const el = document.querySelector('[data-testid="combo-bundles-section"]');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [searchParams, comboProducts.length]);

  const groupKeys = Array.from(groups.keys()).sort((a, b) => categoryPriority(a) - categoryPriority(b));

  const serviceTypes = useMemo(() => {
    const map = new Map();
    groupKeys.forEach((gk) => {
      const st = serviceTypeOf(gk);
      if (!map.has(st)) map.set(st, []);
      map.get(st).push(gk);
    });
    return map;
  }, [groupKeys]);
  const serviceTypeKeys = Array.from(serviceTypes.keys());
  const activeServiceType = (serviceType && serviceTypes.has(serviceType)) ? serviceType : serviceTypeKeys[0];
  const subGroupKeys = serviceTypes.get(activeServiceType) || [];
  const activeServiceTypeLabel = (language === "en" ? SERVICE_TYPE_LABEL_EN : SERVICE_TYPE_LABEL_FR)[activeServiceType] || activeServiceType;

  const activeGroupKey = (groupKey && subGroupKeys.includes(groupKey)) ? groupKey : subGroupKeys[0];
  const activeGroup = groups.get(activeGroupKey);
  const variantKeys = activeGroup ? Array.from(activeGroup.variants.keys()).sort((a, b) => VARIANT_ORDER.indexOf(a) - VARIANT_ORDER.indexOf(b)) : [];
  const activeVariantKey = (activeGroup && activeGroup.variants.has(variantKey)) ? variantKey : variantKeys[0];
  const activeProducts = activeGroup?.variants.get(activeVariantKey)?.products.slice().sort((a, b) => a.quantity - b.quantity) || [];

  const meta = getNetworkMeta(slug);
  const Icon = Icons[meta.icon] || Icons.Globe;

  if (notFound) return <NotFound />;
  if (!network) return <div className="min-h-[50vh] flex items-center justify-center text-zinc-400">{t("common.loading", "Chargement...")}</div>;

  return (
    <div className="relative" data-testid="network-page">
      <SEOHead
        title={`${field(network, "name")} - ${t("network.seo_title_action", "Acheter Followers, Likes, Vues")} | Buy Likes and Followers`}
        description={field(network, "description")}
        schema={[
          {
            "@context": "https://schema.org", "@type": "Product", name: `${field(network, "name")} Followers`,
            description: field(network, "description"),
            offers: activeProducts.slice(0, 1).map((p) => ({ "@type": "Offer", price: p.price, priceCurrency: "EUR" }))[0],
          },
          buildBreadcrumbSchema([{ label: field(network, "name") }]),
        ]}
      />

      <div className="absolute inset-0 -z-10 overflow-hidden" data-testid="network-page-theme-bg">
        <div className={`absolute inset-0 opacity-[0.09] bg-gradient-to-br ${meta.gradient}`} />
        <div className="absolute top-[2%] left-[8%] w-[420px] h-[420px] rounded-full blur-[130px]" style={{ background: meta.glow }} />
        <div className="absolute top-[38%] right-[5%] w-[380px] h-[380px] rounded-full blur-[130px]" style={{ background: meta.glow, opacity: 0.7 }} />
        <div className="absolute top-[68%] left-[15%] w-[400px] h-[400px] rounded-full blur-[130px]" style={{ background: meta.glow, opacity: 0.5 }} />
        <div className="absolute bottom-[0%] right-[20%] w-[350px] h-[350px] rounded-full blur-[130px]" style={{ background: meta.glow, opacity: 0.6 }} />
      </div>

      <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
        <div className={`absolute inset-0 -z-10 opacity-25 bg-gradient-to-br ${meta.gradient}`} />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="mx-auto max-w-4xl text-center relative">
          <div className="text-left mb-6">
            <Breadcrumbs items={[{ label: field(network, "name") }]} />
          </div>
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center mx-auto mb-6 shadow-2xl`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="network-title">{field(network, "name")}</h1>


          <div className="flex flex-wrap items-center justify-center gap-3 mb-5" data-testid="network-trust-pills">
            {[
              { icon: "Eye", label: t("network.trust_pill_visibility", "Plus de visibilité") },
              { icon: "TrendingUp", label: t("network.trust_pill_growth", "Croissance naturelle") },
              { icon: "Lock", label: t("network.trust_pill_protected", "Compte protégé") },
            ].map((pill) => {
              const PillIcon = Icons[pill.icon];
              return (
                <span
                  key={pill.icon}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-950 border border-white/10 text-sm font-semibold text-white"
                  data-testid={`trust-pill-${pill.icon.toLowerCase()}`}
                >
                  <PillIcon className="w-4 h-4" style={{ color: meta.solid }} /> {pill.label}
                </span>
              );
            })}
          </div>

          <p className="text-lg text-zinc-300 mb-3">{field(network, "tagline")}</p>
          <p className="text-sm text-zinc-500 max-w-2xl mx-auto mb-7">{field(network, "description")}</p>

          <button
            onClick={() => document.getElementById("network-services-section")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-bold text-white shadow-2xl transition-transform hover:-translate-y-0.5"
            style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)`, boxShadow: `0 16px 36px -10px ${meta.solid}80` }}
            data-testid="network-hero-cta-btn"
          >
            {t("network.hero_cta_order", "Commander")} <Icons.ArrowDown className="w-4 h-4" />
          </button>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-24" id="network-services-section">
        <div className="mx-auto max-w-6xl">
          <LiveSocialProof category={activeGroupKey} networkName={field(network, "name")} trustCount={network.trust_followers_count || 15000} accentColor={meta.solid} />

          <ComboBundles products={comboProducts} meta={meta} networkName={field(network, "name")} onSelect={(p) => navigate(`/checkout/${p.id}`)} />

          <h2 className="font-heading text-2xl font-bold text-white mb-6">{t("network.services_available", "Services disponibles")}</h2>

          <div className="flex flex-wrap gap-3 mb-5" data-testid="network-service-type-tabs">
            {serviceTypeKeys.map((st) => {
              const StIcon = Icons[SERVICE_TYPE_ICON[st]] || Icons.Package;
              const isActive = activeServiceType === st;
              return (
                <button
                  key={st}
                  onClick={() => { setServiceType(st); setGroupKey(null); setVariantKey(null); setSelected(null); }}
                  data-testid={`service-type-tab-${st}`}
                  className={`flex items-center gap-2.5 px-6 py-3.5 rounded-2xl text-base font-bold transition-all ${
                    isActive ? "text-white shadow-xl scale-[1.03]" : "bg-zinc-800/90 border border-white/15 text-zinc-200 hover:border-white/30 hover:bg-zinc-800"
                  }`}
                  style={isActive ? { background: `linear-gradient(135deg, ${meta.solid}, #a855f7)`, boxShadow: `0 12px 32px -10px ${meta.solid}90` } : undefined}
                >
                  <StIcon className="w-5 h-5" />
                  {(language === "en" ? SERVICE_TYPE_LABEL_EN : SERVICE_TYPE_LABEL_FR)[st] || st}
                </button>
              );
            })}
          </div>

          {subGroupKeys.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-6 pb-6 border-b border-white/8" data-testid="network-subcategory-tabs">
              {subGroupKeys.map((gk) => {
                const g = groups.get(gk);
                const variantList = Array.from(g.variants.values());
                const singleVariant = variantList.length === 1 ? variantList[0] : null;
                const flag = singleVariant?.label === "France" ? VARIANT_FLAG.france : singleVariant?.label === "Europe" ? VARIANT_FLAG.europe : "";
                let subLabel = g.label.replace(new RegExp(`^${activeServiceTypeLabel}\\s*`, "i"), "").trim();
                if (!subLabel) subLabel = singleVariant?.label || g.label;
                const isActive = activeGroupKey === gk;
                return (
                  <button
                    key={gk} onClick={() => { setGroupKey(gk); setVariantKey(null); setSelected(null); }}
                    data-testid={`subcategory-tab-${gk}`}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors flex items-center gap-1.5 ${
                      isActive ? "text-white border-transparent" : "bg-white/8 border-white/15 text-zinc-300 hover:bg-white/12 hover:border-white/25"
                    }`}
                    style={isActive ? { background: `linear-gradient(135deg, ${meta.solid}cc, #a855f7cc)` } : undefined}
                  >
                    {flag && <span>{flag}</span>}
                    {subLabel || t("network.standard", "Standard")}
                  </button>
                );
              })}
            </div>
          )}

          {variantKeys.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="network-variant-pills">
              <span className="text-xs font-medium text-zinc-400 mr-1">{t("network.choose_origin", "Provenance")} :</span>
              {variantKeys.map((vk) => (
                <button
                  key={vk} onClick={() => { setVariantKey(vk); setSelected(null); }}
                  data-testid={`variant-pill-${vk}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 transition-colors flex items-center gap-1.5 ${
                    activeVariantKey === vk ? "border-transparent text-white" : "border-white/15 bg-white/5 text-zinc-300 hover:text-white hover:border-white/30"
                  }`}
                  style={activeVariantKey === vk ? { background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` } : undefined}
                >
                  <span>{VARIANT_FLAG[vk] || ""}</span>
                  {activeGroup.variants.get(vk).label || "Standard"}
                </button>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14" data-testid="network-products-grid">
            {activeProducts.map((p) => (
              <PricingCard key={p.id} product={p} meta={meta} selected={selected?.id === p.id} onSelect={(prod) => navigate(`/checkout/${prod.id}`)} />
            ))}
          </div>

          {selected && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-8 max-w-2xl mx-auto text-center mb-20" data-testid="network-order-cta">
              <p className="text-sm text-zinc-400 mb-4">{field(selected, "category_label")} · {selected.quantity.toLocaleString()} — <span className="text-white font-semibold">{selected.price} €</span></p>
              <Button size="lg" className="rounded-full text-white px-10" style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }} onClick={() => navigate(`/checkout/${selected.id}`)} data-testid="proceed-to-checkout-btn">
                {t("common.order_now", "Commander")}
              </Button>
            </motion.div>
          )}

          {network.content_fr && (
            <div className="max-w-3xl mx-auto mb-20">
              <div
                className="seo-content text-sm leading-relaxed"
                data-testid="network-seo-content"
                dangerouslySetInnerHTML={{ __html: field(network, "content") }}
              />
            </div>
          )}

          <HowItOrderWorks networkName={field(network, "name")} accentColor={meta.solid} />

          <div className="max-w-4xl mx-auto mb-14">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 text-center">{t("network.guarantees_title", "Nos garanties")}</h2>
            <Guarantees networkName={field(network, "name")} accentColor={meta.solid} />
          </div>

          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 text-center">{t("network.comparison_title", "Comment choisir un site fiable ?")}</h2>
            <ComparisonTable name={field(network, "name")} accentColor={meta.solid} />
          </div>

          {faq.length > 0 && (
            <div className="max-w-3xl mx-auto mb-20">
              <h2 className="font-heading text-2xl font-bold text-white mb-6">{t("network.faq_title", "Questions fréquentes")}</h2>
              <Accordion type="single" collapsible className="space-y-3" data-testid="network-faq-accordion">
                {faq.map((it) => (
                  <AccordionItem key={it.id} value={it.id} className="rounded-xl border border-white/8 bg-zinc-900/60 px-5">
                    <AccordionTrigger className="text-sm font-medium text-white">{field(it, "question")}</AccordionTrigger>
                    <AccordionContent className="text-sm text-zinc-500">{field(it, "answer")}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {blogPosts.length > 0 && (
            <div className="max-w-3xl mx-auto mb-14">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">{t("network.guides_title", "Guides populaires")}</h2>
              <div className="flex flex-wrap gap-3" data-testid="network-guides-links">
                {blogPosts.map((b) => (
                  <a key={b.slug} href={`/blog/${b.slug}`} className="text-sm text-indigo-400 hover:underline">{field(b, "title")}</a>
                ))}
              </div>
            </div>
          )}

          {allNetworks.length > 1 && (
            <div className="max-w-4xl mx-auto" data-testid="network-related-links">
              <h2 className="font-heading text-lg font-semibold text-white mb-4 text-center">{t("network.other_networks_title", "Découvrez nos autres réseaux")}</h2>
              <div className="flex flex-wrap justify-center gap-2.5">
                {allNetworks.filter((n) => n.slug !== slug).slice(0, 8).map((n) => {
                  const nMeta = getNetworkMeta(n.slug);
                  const NIcon = Icons[nMeta.icon] || Icons.Globe;
                  return (
                    <a
                      key={n.slug} href={`/reseaux/${n.slug}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:border-white/25 transition-colors"
                      data-testid={`related-network-link-${n.slug}`}
                    >
                      <NIcon className="w-3.5 h-3.5" style={{ color: nMeta.solid }} /> {field(n, "name")}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <ReviewsSection network={slug} networkName={field(network, "name")} accentColor={meta.solid} />

      {selected && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 sm:hidden px-4 py-3 flex items-center justify-between gap-3 bg-zinc-950/95 backdrop-blur-lg border-t border-white/10"
          style={{ boxShadow: `0 -8px 30px -8px ${meta.solid}50` }}
          data-testid="network-sticky-mobile-cta"
        >
          <div className="min-w-0">
            <p className="text-[10px] text-zinc-500 truncate">{field(selected, "category_label")} · {selected.quantity.toLocaleString()}</p>
            <p className="text-lg font-heading font-bold text-white">{selected.price} €</p>
          </div>
          <Button
            className="rounded-full text-white px-6 shrink-0" style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }}
            onClick={() => navigate(`/checkout/${selected.id}`)} data-testid="network-sticky-mobile-cta-btn"
          >
            {t("common.order_now", "Commander")}
          </Button>
        </div>
      )}
    </div>
  );
}
